import React, { useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import Button from 'app/seedly-component-library/button-new';
import buildConfig from 'app/utils/buildConfig';

interface SingSaverLoginButtonProps {
  className?: string;
  variant?: 'primary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
}

const SingSaverLoginButton: React.FC<SingSaverLoginButtonProps> = ({
  className = '',
  variant = 'outline',
  size = 'md',
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSingSaverLogin = async () => {
    try {
      setIsLoading(true);

      // Log the start of authentication process
      Sentry.addBreadcrumb({
        message: 'SingSaver login initiated',
        category: 'auth',
        level: 'info' as any,
      });

      // Request authorization URL from backend (backend generates PKCE)
      const response = await fetch(`${buildConfig.api}v4/auth/keycloak`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          // Pass the correct callback URL for frontend
          'X-Callback-URL': `${window.location.origin}/auth/keycloak/callback`,
        },
      });

      if (!response.ok) {
        const errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        // Log HTTP error to Sentry
        Sentry.captureException(
          new Error(`SingSaver auth URL request failed: ${errorMessage}`),
          {
            tags: {
              component: 'SingSaverLoginButton',
              operation: 'fetch_auth_url',
            },
            extra: {
              status: response.status,
              statusText: response.statusText,
              url: `${buildConfig.api}v4/auth/keycloak`,
            },
          },
        );

        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.success && data.data.authorization_url) {
        // Store code_verifier from backend for callback (if provided)
        if (data.data.code_verifier) {
          sessionStorage.setItem(
            'singsaver_code_verifier',
            data.data.code_verifier,
          );
        }

        // Log successful URL generation
        Sentry.addBreadcrumb({
          message: 'Authorization URL received, redirecting to Keycloak',
          category: 'auth',
          level: 'info' as any,
          data: {
            hasCodeVerifier: !!data.data.code_verifier,
          },
        });

        // Redirect to MoneyHero Keycloak for authentication
        window.location.href = data.data.authorization_url;
      } else {
        const errorMessage = data.error || 'Unknown error';

        // Log API error to Sentry
        Sentry.captureException(
          new Error(`SingSaver login API error: ${errorMessage}`),
          {
            tags: {
              component: 'SingSaverLoginButton',
              operation: 'process_auth_response',
            },
            extra: {
              response: data,
            },
          },
        );

        console.error('SingSaver login failed:', errorMessage);
        alert(`Failed to start SingSaver login: ${errorMessage}`);
      }
    } catch (error) {
      // Log network or unexpected errors to Sentry
      Sentry.captureException(error, {
        tags: {
          component: 'SingSaverLoginButton',
          operation: 'login_flow',
        },
        extra: {
          apiUrl: `${buildConfig.api}v4/auth/keycloak`,
          origin: window.location.origin,
        },
      });

      console.error('Network error during SingSaver login:', error);
      alert(
        `Network error: ${error.message}. Please check your connection and try again.`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleSingSaverLogin}
      className={className}
      data-testid="singsaver-login-button"
      isLoading={isLoading}
      disabled={isLoading}
    >
      {isLoading ? 'Connecting...' : 'Login/Sign up'}
    </Button>
  );
};

export default SingSaverLoginButton;
